import {
  DepartmentCreateRequest,
  DepartmentPutRequest,
} from '~/common/entities/Department/DepartmentRequest';
import { AddEmployeeToDepartmentResponse } from '~/common/entities/Department/AddEmployeeToDepartmentResponse';
import {
  Department,
  Employee,
} from '~/common/entities/Department/DepartmentTypes';
import { DepartmentResponse } from '~/common/entities/Department/DepartmentResponse';
import { useAuthenticationService } from './AuthenticationService';
import { ApiErrorCallback, ApiSuccessCallback } from './callback';
import { authenticatedApi } from '../utils/authenticatedApi';
import { createContainer } from 'unstated-next';
import { useState } from 'react';
import _ from 'lodash';

const initialData = [] as Department[];

function useDepartment() {
  const [departments, setDepartments] = useState(initialData);
  const { authenticationState } = useAuthenticationService();

  const setDepartmentsOrdered = (departments: Department[]) => {
    setDepartments(
      _.sortBy(departments, [
        (department) => department.name.toLocaleLowerCase(),
      ]),
    );
  };

  const addDepartment = async (
    name: string,
    description: string,
    onSuccess?: ApiSuccessCallback<DepartmentResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const values: DepartmentCreateRequest = {
      name,
      description,
    };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).post<DepartmentResponse, DepartmentCreateRequest>('/department/', values);
    if (ApiError && onError) onError(ApiError);
    if (data) {
      setDepartmentsOrdered([...departments, data]);
    }
    if (data && onSuccess) onSuccess(data);
  };

  const overrideDepartmentsLocally = (departments: Department[]) => {
    setDepartmentsOrdered(departments);
  };

  const deleteDepartment = async (
    department: Department,
    onError?: ApiErrorCallback,
  ) => {
    const { error } = await authenticatedApi(authenticationState).delete<{}>(
      `/department/${department.id}/`,
    );
    if (error && onError) onError();
    if (!error)
      setDepartmentsOrdered(departments.filter((d) => d.id !== department.id));
  };

  const changeDepartment = async (
    department: Department,
    name: string,
    description: string,
    onSuccess?: ApiSuccessCallback<DepartmentResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const values: DepartmentPutRequest = {
      name,
      description,
    };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).put<DepartmentResponse, DepartmentPutRequest>(
      `department/${department.id}/`,
      values,
    );
    if (ApiError && onError) {
      return onError(ApiError);
    }
    if (data)
      setDepartmentsOrdered([
        ...departments.filter((d) => d.id !== data.id),
        data,
      ]);
    if (onSuccess) return onSuccess(data);
    return undefined;
  };

  const addEmployeeToDepartment = async (
    employee: Employee,
    department: Department,
    onSuccess?: ApiSuccessCallback<AddEmployeeToDepartmentResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(authenticationState).patch<
      AddEmployeeToDepartmentResponse,
      {}
    >(`/department/${department.id}/employee/${employee.id}/`, {});
    if (error && onError) onError();
    if (data) {
      const departmentInArray: Department | undefined = departments.find(
        (d) => d.id === department.id,
      );
      if (departmentInArray) {
        if (!departmentInArray.employees) {
          departmentInArray.employees = [];
        }
        departmentInArray.employees.push(data);
        setDepartmentsOrdered([...departments]);
      }
    }
    if (data && onSuccess) onSuccess(data);
  };

  const removeEmployeeFromDepartment = async (
    employee: Employee,
    department: Department,
    onSuccess?: ApiSuccessCallback<AddEmployeeToDepartmentResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(
      authenticationState,
    ).delete<AddEmployeeToDepartmentResponse>(
      `/department/${department.id}/employee/${employee.id}/`,
    );
    if (error && onError) {
      onError();
    }
    if (data) {
      const departmentInArray: Department | undefined = departments.find(
        (d) => d.id === department.id,
      );
      if (departmentInArray && departmentInArray.employees) {
        departmentInArray.employees = departmentInArray.employees.filter(
          (e) => data.id !== e.id,
        );
        setDepartmentsOrdered([...departments]);
      }
    }
    if (data && onSuccess) onSuccess(data);
  };

  const fillStoreWithServerData = async (
    onSuccess?: ApiSuccessCallback<Department[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } =
      await authenticatedApi(authenticationState).get<Department[]>(
        '/departments/',
      );
    if (error) return onError ? onError(error) : undefined;
    if (data.length > 0) overrideDepartmentsLocally(data);
    if (data && onSuccess) return onSuccess(data);
    return undefined;
  };

  return {
    departments,
    addDepartment,
    changeDepartment,
    deleteDepartment,
    addEmployeeToDepartment,
    removeEmployeeFromDepartment,
    fillStoreWithServerData,
  };
}

export const {
  Provider: DepartmentProvider,
  useContainer: useDepartmentService,
} = createContainer(useDepartment);
